// Dati della Monthly PM View V2: gerarchia dei riporti e ore/forecast per persona.
import { API_ENDPOINTS } from '../config/api.config';
import { resolveDisplayName } from './absenceCalendarExport';
import { fetchMonthlyWorklogSummaryForUsers } from './peopleManagerApi';
import { getAbsencesForUsers } from './absenceService';

async function fetchDirectReports(managerId) {
  const res = await fetch(`${API_ENDPOINTS.USERS}?id_people_manager=${managerId}`);
  if (!res.ok) throw new Error(`Unable to load reports of manager ${managerId} (${res.status})`);
  const data = await res.json();
  return data.users || [];
}

/**
 * Gerarchia completa sotto rootId, caricata un livello alla volta (richieste del livello in parallelo).
 * Tollera cicli in id_people_manager.
 * @returns {Promise<Array<{ id, name, email, managerId, isPeopleManager, depth }>>}
 */
export async function fetchHierarchy(rootId) {
  const people = new Map();
  const visitedManagers = new Set([String(rootId)]);
  let level = [rootId];
  let depth = 0;
  while (level.length) {
    const results = await Promise.all(level.map((id) => fetchDirectReports(id).then((users) => ({ id, users }))));
    const next = [];
    const levelDepth = depth;
    results.forEach(({ id: managerId, users }) => {
      users.forEach((u) => {
        const key = String(u.id);
        if (key === String(rootId) || people.has(key)) return;
        people.set(key, {
          id: u.id,
          name: resolveDisplayName(u),
          email: u.email,
          managerId,
          isPeopleManager: !!u.is_people_manager,
          depth: levelDepth,
        });
        if (u.is_people_manager && !visitedManagers.has(key)) {
          visitedManagers.add(key);
          next.push(u.id);
        }
      });
    });
    level = next;
    depth += 1;
  }
  return [...people.values()];
}

const dayOf = (workDate) => parseInt(String(workDate).slice(8, 10), 10);

function addCell(days, day, actual, forecast) {
  const cell = days[day] || { actual: 0, forecast: 0 };
  cell.actual += actual;
  cell.forecast += forecast;
  days[day] = cell;
}

/**
 * Ore del mese per persona: totali per giorno e dettaglio per progetto (consuntivo e forecast).
 * @returns {Promise<Record<string, { days, projects: Record<string, { name, days }> }>>}
 */
export async function fetchPeopleMonth(userIds, yearMonth, activityIds = []) {
  if (!userIds.length) return {};
  const rows = await fetchMonthlyWorklogSummaryForUsers(userIds, yearMonth, activityIds);
  const out = {};
  rows.forEach((r) => {
    const uid = String(r.user_id);
    if (!out[uid]) out[uid] = { days: {}, projects: {} };
    const actual = Number(r.actual_effort ?? r.total_effort) || 0;
    const forecast = Number(r.forecast_effort) || 0;
    const day = dayOf(r.work_date);
    addCell(out[uid].days, day, actual, forecast);
    const pid = String(r.project_id ?? r.project_name);
    if (!out[uid].projects[pid]) out[uid].projects[pid] = { name: r.project_name || 'Unknown project', days: {} };
    addCell(out[uid].projects[pid].days, day, actual, forecast);
  });
  return out;
}

/** Assenze del mese: { [userId]: Map(day → absence) }. */
export async function fetchAbsencesMap(userIds, yearMonth) {
  if (!userIds.length) return {};
  const entries = await getAbsencesForUsers(userIds, yearMonth);
  const map = {};
  entries.forEach((a) => {
    const uid = String(a.user_id);
    if (!map[uid]) map[uid] = new Map();
    map[uid].set(dayOf(a.date), a);
  });
  return map;
}

// ---- Cache dei mesi già caricati (e precaricati), per cambiare mese senza attese ----
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_MAX_ENTRIES = 12;
const monthCache = new Map();

const bundleKey = (userIds, yearMonth, activityIds) =>
  `${userIds.join(',')}|${yearMonth}|${[...activityIds].sort().join(',')}`;

/**
 * Ore (filtrate e, se serve, su tutte le attività) e assenze del mese, con cache in memoria.
 * @returns {{ promise: Promise<{ filtered, all, absences }>, value?: { filtered, all, absences } }}
 */
export function loadPeopleMonthBundle(userIds, yearMonth, activityIds = []) {
  const key = bundleKey(userIds, yearMonth, activityIds);
  const hit = monthCache.get(key);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit;

  const entry = { at: Date.now(), value: undefined };
  entry.promise = Promise.all([
    fetchPeopleMonth(userIds, yearMonth, activityIds),
    // La completezza si calcola sempre su tutte le attività
    activityIds.length ? fetchPeopleMonth(userIds, yearMonth, []) : Promise.resolve(null),
    fetchAbsencesMap(userIds, yearMonth),
  ])
    .then(([filtered, all, absences]) => {
      entry.value = { filtered, all, absences };
      return entry.value;
    })
    .catch((e) => {
      monthCache.delete(key);
      throw e;
    });
  monthCache.set(key, entry);
  if (monthCache.size > CACHE_MAX_ENTRIES) monthCache.delete(monthCache.keys().next().value);
  return entry;
}

/** Dati del mese già pronti in cache, altrimenti null. */
export function peekPeopleMonthBundle(userIds, yearMonth, activityIds = []) {
  const hit = monthCache.get(bundleKey(userIds, yearMonth, activityIds));
  return hit && hit.value && Date.now() - hit.at < CACHE_TTL_MS ? hit.value : null;
}

/** Scarta dalla cache i mesi di queste persone (dopo un salvataggio o una modifica delle assenze). */
export function invalidatePeopleMonth(userIds, yearMonth) {
  const prefix = `${userIds.join(',')}|${yearMonth}|`;
  [...monthCache.keys()].forEach((k) => {
    if (k.startsWith(prefix)) monthCache.delete(k);
  });
}
